import {useSyncExternalStore} from "react";

type InstallPromptOutcome = "accepted" | "dismissed";
type ManualInstallMode = "ios-safari" | "ios-browser" | null;

type InstallPromptResult = {
  outcome: InstallPromptOutcome;
  platform: string;
};

export interface DeferredInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<InstallPromptResult>;
  prompt: () => Promise<InstallPromptResult>;
}

type InstallPromptState = {
  deferredPrompt: DeferredInstallPromptEvent | null;
  isInstalled: boolean;
  manualInstallMode: ManualInstallMode;
};

type InstallPromptStore = {
  initialized: boolean;
  listeners: Set<() => void>;
  state: InstallPromptState;
};

declare global {
  interface Navigator {
    standalone?: boolean;
  }

  interface Window {
    __chaykaInstallPromptStore__?: InstallPromptStore;
  }
}

const createStore = (): InstallPromptStore => ({
  initialized: false,
  listeners: new Set(),
  state: {
    deferredPrompt: null,
    isInstalled: false,
    manualInstallMode: null,
  },
});

const installPromptStore = (() => {
  if (typeof window === "undefined") {
    return createStore();
  }

  if (!window.__chaykaInstallPromptStore__) {
    window.__chaykaInstallPromptStore__ = createStore();
  }

  return window.__chaykaInstallPromptStore__;
})();

const notifyListeners = () => {
  for (const listener of installPromptStore.listeners) {
    listener();
  }
};

const isDeferredInstallPromptEvent = (event: Event): event is DeferredInstallPromptEvent => {
  return "prompt" in event && typeof event.prompt === "function";
};

const isIosDevice = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const {userAgent, platform, maxTouchPoints} = window.navigator;

  return /iPad|iPhone|iPod/i.test(userAgent) || (platform === "MacIntel" && maxTouchPoints > 1);
};

const isSafariBrowser = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const {userAgent} = window.navigator;

  return (
    /Safari/i.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo|YaBrowser/i.test(userAgent)
  );
};

const getIsInstalled = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true
  );
};

const getManualInstallMode = (): ManualInstallMode => {
  if (getIsInstalled() || !isIosDevice()) {
    return null;
  }

  return isSafariBrowser() ? "ios-safari" : "ios-browser";
};

const getInstallPromptState = (
  deferredPrompt: DeferredInstallPromptEvent | null,
): InstallPromptState => ({
  deferredPrompt,
  isInstalled: getIsInstalled(),
  manualInstallMode: getManualInstallMode(),
});

if (typeof window !== "undefined" && !installPromptStore.initialized) {
  installPromptStore.initialized = true;
  installPromptStore.state = getInstallPromptState(null);

  window.addEventListener("beforeinstallprompt", (event) => {
    if (!isDeferredInstallPromptEvent(event)) {
      return;
    }

    event.preventDefault();
    installPromptStore.state = getInstallPromptState(event);
    notifyListeners();
  });

  window.addEventListener("appinstalled", () => {
    installPromptStore.state = getInstallPromptState(null);
    notifyListeners();
  });
}

const subscribe = (listener: () => void) => {
  installPromptStore.listeners.add(listener);

  return () => {
    installPromptStore.listeners.delete(listener);
  };
};

const getSnapshot = () => installPromptStore.state;

export const usePwaInstallPrompt = () => {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

export const requestPwaInstall = async () => {
  const deferredPrompt = installPromptStore.state.deferredPrompt;

  if (!deferredPrompt) {
    return null;
  }

  installPromptStore.state = getInstallPromptState(null);
  notifyListeners();

  return deferredPrompt.prompt();
};
